import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { useState } from 'react'
import { adminApi } from '@/utils/api'
import { Button } from '@/components/ui/button'

export default function AdminReportsPage() {
  const { user } = useAuth()
  const displayName = getUserDisplayName(user)
  const initials = getUserInitials(user)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [reportType, setReportType] = useState('users')
  const [summary, setSummary] = useState<unknown | null>(null)
  const [exportLink, setExportLink] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSummary = async () => {
    if (!startDate || !endDate) {
      setError('Select start and end dates')
      return
    }
    setLoading(true)
    setError(null)
    setExportLink(null)
    try {
      const res = await adminApi.reportsSummary({ startDate, endDate })
      setSummary((res as { data?: unknown }).data || res)
    } catch (err) {
      setError((err as Error)?.message || 'Failed to generate summary')
    } finally {
      setLoading(false)
    }
  }

  const handleExport = async () => {
    if (!reportType) return
    setLoading(true)
    setError(null)
    try {
      const res = await adminApi.reportsExport({ type: reportType, format: 'json' })
      setExportLink(typeof res === 'string' ? res : JSON.stringify(res))
    } catch (err) {
      setError((err as Error)?.message || 'Failed to export')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='min-h-screen bg-[#edeff3]'>
      <div className='p-4 md:p-6 lg:p-8'>
        <div className='bg-white rounded-[24px] p-4 sm:p-6 md:p-8 space-y-6'>
          {/* Header */}
          <div className='flex items-center justify-between'>
            <div>
              <h1 className='text-[24px] sm:text-[32px] font-bold text-[#151515]'>Reports</h1>
              <p className='text-[13px] sm:text-[14px] text-[#4e4d4d]'>Summary and export endpoints.</p>
            </div>
            <div className='hidden lg:flex items-center gap-3'>
              <div className='h-[47px] w-[48px] rounded-full bg-[#434343] flex items-center justify-center text-white font-bold text-[16px]'>
                {initials}
              </div>
              <span className='text-[24px] font-bold text-[#353535]'>{displayName}</span>
            </div>
          </div>

          {/* Report Controls */}
          <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4 sm:p-6 space-y-4'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <label className='block text-[14px] font-medium text-[#151515] text-start mb-2'>Start Date</label>
                <input
                  type='date'
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className='w-full rounded-[8px] border border-[#E5E7EB] px-3 py-2.5 text-[14px] text-[#151515] focus:outline-none focus:ring-1 focus:ring-[#FF6600]'
                  required
                />
              </div>
              <div>
                <label className='block text-[14px] font-medium text-[#151515] text-start mb-2'>End Date</label>
                <input
                  type='date'
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className='w-full rounded-[8px] border border-[#E5E7EB] px-3 py-2.5 text-[14px] text-[#151515] focus:outline-none focus:ring-1 focus:ring-[#FF6600]'
                  required
                />
              </div>
            </div>

            <div className='flex flex-wrap gap-2'>
              {['users', 'organizations', 'couples', 'assessments', 'all'].map((type) => (
                <button
                  key={type}
                  className={`px-3 sm:px-4 py-2 rounded-[8px] text-[13px] sm:text-[14px] font-bold transition-colors capitalize ${
                    reportType === type
                      ? 'bg-[#151515] text-white'
                      : 'bg-white text-[#151515] border border-[#e0e0e0] hover:bg-gray-50'
                  }`}
                  onClick={() => setReportType(type)}
                  aria-pressed={reportType === type}
                >
                  {type}
                </button>
              ))}
            </div>

            <div className='flex gap-3'>
              <Button
                className='bg-[#151515] hover:bg-[#2a2a2a] font-bold rounded-[8px]'
                onClick={handleSummary}
                disabled={loading || !startDate || !endDate}
              >
                {loading ? 'Working...' : 'Generate Summary'}
              </Button>
              <Button
                variant='outline'
                className='border-[#FF6600] text-[#FF6600] rounded-[8px] font-bold hover:bg-orange-50'
                onClick={handleExport}
                disabled={loading || !reportType}
              >
                Export
              </Button>
            </div>
          </div>

          {/* Report Preview */}
          <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4 sm:p-6 space-y-3'>
            <h3 className='text-[18px] font-bold text-[#151515]'>Report Preview</h3>
            {error && <p className='text-[14px] text-red-600'>{error}</p>}
            {!summary && !exportLink && (
              <p className='text-[14px] text-[#4e4d4d]'>Generate a summary or export to see results.</p>
            )}

            {(summary && typeof summary === 'object' && (
              <div className='space-y-3'>
                {'period' in (summary as Record<string, unknown>) && (
                  <div className='flex gap-4 text-[12px] text-[#4e4d4d]'>
                    <div><span className='font-bold text-[#151515]'>Start:</span> {String((summary as any).period?.startDate || '')}</div>
                    <div><span className='font-bold text-[#151515]'>End:</span> {String((summary as any).period?.endDate || '')}</div>
                  </div>
                )}
                {'summary' in (summary as Record<string, unknown>) && (
                  <div className='grid grid-cols-2 sm:grid-cols-3 gap-3'>
                    {Object.entries((summary as any).summary || {}).map(([label, value]) => (
                      <div key={label} className='bg-white rounded-[10px] border border-[#e8e8e8] p-3'>
                        <p className='font-bold text-[#151515] text-[12px] capitalize'>{label.replace(/([A-Z])/g, ' $1').trim()}</p>
                        <p className='text-[24px] font-bold text-[#151515] mt-1'>{String(value ?? 0)}</p>
                      </div>
                    ))}
                  </div>
                )}
                {'userBreakdown' in (summary as Record<string, unknown>) && (
                  <div>
                    <p className='font-bold text-[#151515] text-[14px] mb-2'>Users by role</p>
                    <div className='flex flex-wrap gap-2'>
                      {Object.entries((summary as any).userBreakdown?.byRole || {}).map(([role, count]) => (
                        <span key={role} className='px-3 py-1 rounded-[8px] bg-white border border-[#e8e8e8] text-[12px] text-[#151515]'>
                          {role}: {count as string}
                        </span>
                      ))}
                      {Object.keys((summary as any).userBreakdown?.byRole || {}).length === 0 && (
                        <span className='text-[#4e4d4d] text-[12px]'>No user breakdown.</span>
                      )}
                    </div>
                  </div>
                )}
                {'organizationBreakdown' in (summary as Record<string, unknown>) && (
                  <div>
                    <p className='font-bold text-[#151515] text-[14px] mb-2'>Organizations by status</p>
                    <div className='flex flex-wrap gap-2'>
                      {Object.entries((summary as any).organizationBreakdown?.byStatus || {}).map(([status, count]) => (
                        <span key={status} className='px-3 py-1 rounded-[8px] bg-white border border-[#e8e8e8] text-[12px] text-[#151515]'>
                          {status}: {count as string}
                        </span>
                      ))}
                      {Object.keys((summary as any).organizationBreakdown?.byStatus || {}).length === 0 && (
                        <span className='text-[#4e4d4d] text-[12px]'>No organization breakdown.</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )) as React.ReactNode}

            {exportLink && (
              <div>
                <p className='font-bold text-[#151515] text-[14px]'>Export Result</p>
                <div className='mt-1 p-3 bg-white rounded-[10px] border border-[#e8e8e8] text-[12px] text-[#151515] break-words'>
                  {exportLink}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
